import logo from "@/assets/img/RECERQA_logo_white.png";
import help from "@/assets/img/help.svg";
import local_shipping from "@/assets/img/local_shipping.svg";
import notifications from "@/assets/img/notifications.svg";
import { ListItems } from "@/components/ui-elements/SideMenu/ListItems";
import { UserMenu } from "@/components/ui-parts/Header/UserMenu";
import { useAuthStore } from "@/store/auth/authUserStore";
import type { CustomTheme } from "@/theme/theme";
import {
  Box,
  IconButton,
  Link,
  Menu,
  MenuItem,
  MenuList,
  Toolbar,
} from "@mui/material";
import type { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import MuiAppBar from "@mui/material/AppBar";
import MuiDrawer from "@mui/material/Drawer";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/system";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}
interface AppMenuProps {
  attachListItem?: boolean;
}

const drawerWidth = 270;
const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  boxShadow: "none",
  borderBottom: "0",
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    backgroundColor: theme.palette.background.default,
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    border: "none",
    overflowX: "hidden",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(7),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(9),
      },
    }),
  },
}));

export const AppMenu = ({ attachListItem = true }: AppMenuProps) => {
  const { user } = useAuthStore();
  const isAdmin = user?.role == "admin" || user?.role == "superadmin";
  const isIpRestrictionAvailable =
    user?.organization?.is_ip_restriction_available;
  const [open, setOpen] = useState(true);
  const location = useLocation();
  const theme: CustomTheme = useTheme();

  const buttonStyle = {
    width: "30px",
    height: "30px",
    borderRadius: "4px",
    color: "white",
    backgroundColor: theme.palette.secondary.dark,
    marginRight: "12px",
    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
    },
  };

  // TODO：この部分はリファクタリングする
  const isDataCheckPage = location.pathname.match(
    /\/workflow\/[^\/]+\/dataCheck/
  );

  const isFileRegisterPage = location.pathname.match(
    /\/workflow\/[^\/]+\/register/
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuIsOpen = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (isDataCheckPage || isFileRegisterPage) {
      setOpen(false);
    }
  }, [location.pathname]);

  return (
    <>
      <AppBar
        style={{
          background: theme.palette.primary.main,
          // 一旦Drawerを閉じた状態で表示
          width: "100%",
          height: "56px",
          borderBottom: "1px solid #E5E5E5",
        }}
        position="absolute"
        open={open}
      >
        <Toolbar
          sx={{
            pr: "24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            "&.MuiToolbar-root": {
              minHeight: "56px",
            },
          }}
        >
          {/* 企業ロゴ */}
          <Box>
            <Link href="/views/">
              <img
                src={logo}
                style={{
                  width: "108px",
                  verticalAlign: "middle",
                }}
              />
            </Link>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <IconButton
              sx={{
                ...buttonStyle,
                "& .material-symbols-outlined": {
                  fontSize: "20px",
                },
              }}
              onClick={handleMenuClick}
            >
              <img
                src={local_shipping}
                style={{
                  width: "20px",
                }}
              />
            </IconButton>
            <IconButton
              component="a"
              href="https://recerqa.notion.site/RECERQA-Scan-7e084d1c3a494f2c8c6352c4575cd11c"
              target="_blank"
              sx={{
                ...buttonStyle,
                "& .material-symbols-outlined": {
                  fontSize: "20px",
                },
              }}
            >
              <img
                src={notifications}
                style={{
                  width: "20px",
                }}
              />
            </IconButton>
            <IconButton
              component="a"
              href="https://share.hsforms.com/1WnG0yykMTrekU398-NXLwAns1gl"
              target="_blank"
              sx={{
                ...buttonStyle,
                "& .material-symbols-outlined": {
                  fontSize: "20px",
                },
              }}
            >
              <img
                src={help}
                style={{
                  width: "20px",
                }}
              />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuIsOpen}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: -10,
                horizontal: 30,
              }}
              sx={{
                "& .MuiPaper-root": {
                  minWidth: "200px",
                },
              }}
            >
              <MenuList>
                {isAdmin && (
                  <>
                    <MenuItem
                      onClick={() => window.location.replace("/users/")}
                    >
                      アカウント管理
                    </MenuItem>
                    <MenuItem
                      onClick={() =>
                        window.location.replace("/PasswordSetting/")
                      }
                    >
                      パスワードの有効期限設定
                    </MenuItem>
                    {isIpRestrictionAvailable && (
                      <MenuItem
                        onClick={() =>
                          window.location.replace("/IPAddressSetting/")
                        }
                      >
                        IPアドレス設定
                      </MenuItem>
                    )}
                  </>
                )}
                <MenuItem
                  onClick={() => window.location.replace("/dictionarySetting/")}
                >
                  辞書設定
                </MenuItem>
              </MenuList>
            </Menu>
            <Box>
              <UserMenu />
            </Box>
          </Box>
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: [3],
            backgroundColor: theme.palette.background.default,
          }}
        ></Toolbar>
        {attachListItem ? <ListItems /> : null}
      </Drawer>
    </>
  );
};
